import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, catchError } from 'rxjs';
import { DataService } from '../data.service';
import { RatesSroMaster } from '../../shared/common-components/common-structure';

@Injectable({
  providedIn: 'root'
})
export class RatesSroMasterService extends DataService {

  private baseURI = this.apiUri + '/ratessromaster/';
  private baseURIPublic =  this.apiUri + '/public/ratessromaster/';
  public observable$: any;

  public frtRateQueryFormState = new BehaviorSubject<any>(null);
  constructor(http: HttpClient) {
    super(http);
  }

  getSroListByAgencyMasterId(agencyMasterId: number): Observable<RatesSroMaster[]> {
    this.setUri(this.baseURI + agencyMasterId + '/agencymasterid');
    return this.get().pipe(catchError(this.handleError));
  }

  // Purpose of this function is to get List of SroId's for List of SroMasterId's
  getBySroMasterId(sroMasterId: any): Observable<RatesSroMaster[]> {
    this.setUri(this.baseURI + '/sromasterid');
    let params: HttpParams = new HttpParams();

    params = params.append('sroMasterId', sroMasterId);
    return this.get({ params }).pipe(catchError(this.handleError));
  }

  getSroListByAgencyMasterIdAndLocationCode(agencyMasterId: number, agencyId: string): Observable<RatesSroMaster[]> {
    this.setUri(this.baseURI + agencyMasterId + '/agencymasterid/' + agencyId + '/agencyId');
    return this.get().pipe(catchError(this.handleError));
  }

  // gives SRO object when you pass sroId
  getSroMasterDetails(sroId: string, businessType: string): Observable<RatesSroMaster> {
    this.setUri(this.baseURI + 'businessType/' + businessType + '/sroid/' + sroId);
    return this.get().pipe(catchError(this.handleError));
  }

  saveRatesSro(saveRatesSro: RatesSroMaster): Observable<any> {
      return this.http.post(this.baseURI + 'saveratessro', saveRatesSro);
  }

  deleteRatesSro(sroMasterId: number): Observable<any> {
    this.setUri(this.baseURI + 'deleteratessro');
    return this.update(sroMasterId);
  }

  getBySroType(sroType): Observable<any> {
    let params = new HttpParams();
    params = params.set('sroType', sroType);
    this.setUri(this.baseURI + 'srotype/');
    return this.get({ params });
  }

  /**
   * @description returns http client get api call to get the list of sromasterId's based on business type
   * @param businessType: HHG or FRT
   * @returns: { Observable<Object>} : list of sroId's
   */
  getSroTypeByBusinessType(sroType) {
    return this.http.get(this.baseURI + 'srotypepojo/' + sroType + '/businesstype/frt');
  }

  getUspsZipCityState(zipCode): Observable<any> {
    let params = new HttpParams();
    params = params.set('zipCode', zipCode);
    this.setUri(this.baseURI + 'usps/');
    return this.get({ params });
  }

  getCities(stateAbbrev): Observable<any> {
    let params = new HttpParams();
    params = params.set('stateAbbrev', stateAbbrev);
    this.setUri(this.baseURI + 'usps/cities/');
    return this.get({ params });
  }

  getZipCode(stateAbbrev, cityName): Observable<any> {
    let params = new HttpParams();
    params = params.append('stateAbbrev', stateAbbrev);
    params = params.append('cityName', cityName);
    this.setUri(this.baseURI + 'usps/zipcode/');
    return this.get({ params });
  }

  getLocations(cityName): Observable<any> {
    let params = new HttpParams();
    params = params.set('cityName', cityName);
    this.setUri(this.baseURIPublic + 'usps/locations/');
    return this.get({ params });
  }

  getLocation(cityName, zipCode, stateAbbrev): Observable<any> {
    let params = new HttpParams();
    params = params.append('cityName', cityName);
    params = params.append('zipCode', zipCode);
    params = params.append('stateAbbrev', stateAbbrev);
    this.setUri(this.baseURI + 'usps/location/');
    return this.get({ params });
  }

  getMiles(originZipCode, destinationZipCode, businessType, pickUpDate): Observable<any> {
    let params = new HttpParams();
    params = params.append('orig', originZipCode);
    params = params.append('dest', destinationZipCode);
    params = params.append('businessType', businessType);
    params = params.append('pickupDate', pickUpDate);
    this.setUri(this.apiUri + '/pcmiler/getmiles/');
    return this.get({ params });
  }

  // Get SROs by BUSINESS_TYPE = HHG and SRO_TYPE = Alternating or 'Non-Alternating'.
  getSroLlistByBusinessTypeAndSroTypeAndPickupDate(sroType: string, pickupDate : Date): Observable<any> {
    let pickupD = Math.floor(pickupDate.getTime() / 1000);
    return this.http.get(this.baseURI + 'srotypepojo/' + sroType + '/businesstype/hhg/'+ pickupD);
  }

  getSroLlistByBusinessTypeAndSroType(sroType: string): Observable<any> {
    return this.http.get(this.baseURI + 'srotypepojo/' + sroType + '/businesstype/hhg');
  }

  getSroListByRateTypeId(rateTypeId: number): Observable<RatesSroMaster[]> {
    this.setUri(this.baseURI + '/rateTypeId/' + rateTypeId);
    return this.get().pipe(catchError(this.handleError));
    // return of([<RatesSroMaster>{ sroMasterId: 1, sroId: 'sro1' }, <RatesSroMaster>{ sroMasterId: 2, sroId: 'sro2' }]);
  }

  sroTypesByBusinessType(businessType: string): Observable<any[]> {
    this.setUri(this.baseURI + 'businessType/' + businessType + '/ratetypesbybusinesstype');
    return this.get().pipe(catchError(this.handleError));
  }

  sroIDsByBusinessType(businessType: string): Observable<any[]> {
    this.setUri(this.baseURI + 'businessType/' + businessType + '/sroIDsbybusinesstype');
    return this.get().pipe(catchError(this.handleError));
  }

  getPublicLocations(cityName): Observable<any> {
    let params = new HttpParams();
    params = params.set('cityName', cityName);
    this.setUri(this.baseURIPublic + 'usps/locations/');
    return this.get({ params });
  }

  getSROsBybusinessTypeAndRateTypeId(businessType: string, rateTypeId: number): Observable<any[]> {
    this.setUri(this.baseURI + 'businessType/' + businessType + '/rateTypeId/' + rateTypeId);
    return this.get().pipe(catchError(this.handleError));
  }

}
